import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import { setIntendedReferrerContactId, setIntendedReferrerCompanyId } from '../store/slices/intendedInfoSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { isNonLocalNonLoggedInVisitor, dateStringForDateTime } from '../utils/utils';

import DA from '../store/ReduxAccess';

import CampaignTableAppBar from './CampaignTableAppBar';
import CampaignModal from './CampaignModal';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

const dateColumns = ['startDate', 'endDate'];

function desc(a, b, orderBy) {
  if (dateColumns.indexOf(orderBy) >= 0) {
    if (b[orderBy].valueOf() < a[orderBy].valueOf()) {
      return -1;
    }
    if (b[orderBy].valueOf() > a[orderBy].valueOf()) {
      return 1;
    }
    return 0;
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const columns = [
  { id: 'name', numeric: false, label: 'Name' },
  { id: 'startDate', numeric: true, label: 'Start Date' },
  { id: 'endDate', numeric: true, label: 'End Date' },
  { id: 'description', numeric: false, label: 'Description', notSortable: true },
];

const localHeadStyles = (theme) => ({
  ...tableHeadStyles(theme),
  columnClass0: {
    // name
    width: '30%',
  },
  columnClass1: {
    // startDate
    width: '15%',
    minWidth: 130,
  },
  columnClass2: {
    // endDate
    width: '15%',
  },
  columnClass3: {
    // description
    width: '40%',
  },
});

class CampaignTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const { classes, order, orderBy } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {columns.map(
            (column, i) => (
              <TableCell
                key={column.id}
                className={classes['columnClass' + i]}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.notSortable ? (
                  column.label
                ) : (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={order}
                      onClick={this.createSortHandler(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

CampaignTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
};

const StyledTableHead = withStyles(localHeadStyles)(CampaignTableHead);

const styles = (theme) => ({
  ...pageTableStyles(theme),
  root: {
    width: '100%',
    '& .MuiButton-root': {
      fontSize: '1.1rem',
    },
  },
});

class CampaignTable extends React.Component {
  state = {
    order: 'desc',
    orderBy: 'startDate',
    includeCompanies: true,
    includeOthers: true,
    shouldShowCampaignEditor: false,
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  handleCampaignClick = (event, campaignId) => {
    this.showCampaignEditor(campaignId);
  };

  showCampaignEditor(campaignId) {
    this.setState({ shouldShowCampaignEditor: true, campaignToEdit: campaignId });
  }

  closeCampaignEditor() {
    this.setState({ shouldShowCampaignEditor: false, campaignToEdit: 0 });
  }

  handleCompanyClick = (firmId) => {
    this.props.setIntendedReferrerCompanyId(firmId);
    this.setState({ redirect: 'referrerOrg' });
  };

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const { order, orderBy } = this.state;
    const campaigns = DA.getAllCampaigns();
    const summary = `All Campaigns (${campaigns.length})`;
    const sortedCampaigns = stableSort(campaigns, getSorting(order, orderBy));

    return (
      <Paper className={classes.root}>
        <CampaignModal
          show={this.state.shouldShowCampaignEditor}
          campaignId={this.state.campaignToEdit}
          closeModalFunc={() => this.closeCampaignEditor()}
        />

        <CampaignTableAppBar
          summary={summary}
          newButtonLabel="New Campaign"
          newButtonFunc={() => this.showCampaignEditor(0)}
          campaigns={campaigns}
          onAutocompleteChange={(campaign) => this.handleCampaignClick(null, campaign.campaignId, campaign.personId)}
        />
        <div className={classes.tableWrapper}>
          <Table stickyHeader className={classes.table} aria-labelledby="tableTitle">
            <StyledTableHead order={order} orderBy={orderBy} onRequestSort={this.handleRequestSort} />
            <TableBody>
              {sortedCampaigns.map((entry, i) => {
                return (
                  <TableRow
                    tabIndex={-1}
                    key={entry.campaignId}
                    id={'campaign-row-' + entry.campaignId}
                    style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                  >
                    <TableCell
                      className={classes.clickableCell}
                      component="th"
                      scope="row"
                      padding="normal"
                      onClick={(event) => this.handleCampaignClick(event, entry.campaignId, entry.personId)}
                    >
                      {entry.name}
                    </TableCell>
                    <TableCell align="right">{dateStringForDateTime(entry.startDate)}</TableCell>
                    <TableCell align="right">{dateStringForDateTime(entry.endDate)}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Paper>
    );
  }
}

CampaignTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const actionProducers = { setApiResponseInfo, setIntendedReferrerContactId, setIntendedReferrerCompanyId };
export default connect(null, actionProducers)(withStyles(styles)(CampaignTable));
